import { useEffect, useRef, useState } from "react"

export function useBrackyScale() {
  const getBaselineScale = () => {
    if (typeof window === "undefined") return 1.06

    const w = window.innerWidth
    if (w >= 1536) return 1.8
    if (w >= 1280) return 1.65
    if (w >= 1024) return 1.5
    if (w >= 768) return 1.35
    if (w >= 640) return 1.2
    return 1.06
  }

  const [baselineScale, setBaselineScale] = useState<number>(getBaselineScale())
  const [multiplier, setMultiplier] = useState<number>(1)
  const [isAvatarHovered, setIsAvatarHovered] = useState<boolean>(false)
  const [isAvatarPressed, setIsAvatarPressed] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== "undefined" ? window.innerWidth < 640 : false)

  const prevScrollYRef = useRef<number>(typeof window !== "undefined" ? window.scrollY : 0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tickingRef = useRef(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      setBaselineScale(getBaselineScale())
      setIsMobile(window.innerWidth < 640)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleScroll = () => {
      if (tickingRef.current) return

      tickingRef.current = true
      requestAnimationFrame(() => {
        const currentY = window.scrollY
        const delta = currentY - prevScrollYRef.current

        if (Math.abs(delta) > 8) {
          setMultiplier(delta > 0 ? 1.11 : 0.94)
        }

        prevScrollYRef.current = currentY

        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
        scrollTimeoutRef.current = setTimeout(() => {
          setMultiplier(1)
        }, 220) as unknown as NodeJS.Timeout

        tickingRef.current = false
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  const HOVER_MULTIPLIER = 1.11
  const activeMultiplier = isAvatarPressed ? 1 : isAvatarHovered ? HOVER_MULTIPLIER : multiplier

  return {
    baselineScale,
    activeMultiplier,
    isMobile,
    setIsAvatarHovered,
    setIsAvatarPressed,
    computedScale: activeMultiplier,
  }
}
